<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { doc, setDoc, deleteDoc, updateDoc, increment, getDoc } from 'firebase/firestore';
  import { FEATURE_REQUEST_STATUSES, FEATURE_REQUEST_CATEGORIES, type FeatureRequest } from '../../lib/types';

  type Props = {
    request: FeatureRequest;
    onSelect: (request: FeatureRequest) => void;
  };

  let { request, onSelect }: Props = $props();

  let voting = $state(false);
  let hasVoted = $state(false);
  let checkingVote = $state(true);

  // Check if user has already voted
  $effect(() => {
    if (authState.user) {
      checkUserVote();
    } else {
      checkingVote = false;
    }
  });

  async function checkUserVote() {
    if (!authState.user) return;
    try {
      const voteRef = doc(db, 'featureRequestVotes', request.id, 'votes', authState.user.uid);
      const voteDoc = await getDoc(voteRef);
      hasVoted = voteDoc.exists();
    } catch (err) {
      console.error('Error checking vote:', err);
    } finally {
      checkingVote = false;
    }
  }

  async function handleVote(e: Event) {
    e.stopPropagation();

    if (!authState.user || voting || checkingVote) return;

    voting = true;
    try {
      const voteRef = doc(db, 'featureRequestVotes', request.id, 'votes', authState.user.uid);
      const requestRef = doc(db, 'featureRequests', request.id);

      if (hasVoted) {
        // Remove vote
        await deleteDoc(voteRef);
        await updateDoc(requestRef, {
          voteCount: increment(-1),
          updatedAt: new Date(),
        });
        hasVoted = false;
      } else {
        // Add vote
        await setDoc(voteRef, {
          oderId: authState.user.uid,
          vote: 1,
          votedAt: new Date(),
        });
        await updateDoc(requestRef, {
          voteCount: increment(1),
          updatedAt: new Date(),
        });
        hasVoted = true;
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      voting = false;
    }
  }

  function getStatusInfo(status: string) {
    return FEATURE_REQUEST_STATUSES.find(s => s.id === status) || FEATURE_REQUEST_STATUSES[0];
  }

  function getCategoryLabel(categoryId: string) {
    return FEATURE_REQUEST_CATEGORIES.find(c => c.id === categoryId)?.label || categoryId;
  }

  function formatDate(date: Date | string) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }
</script>

<div class="card" role="button" tabindex="0" onclick={() => onSelect(request)} onkeydown={(e) => e.key === 'Enter' && onSelect(request)}>
  <div class="vote-section">
    <button
      class="vote-btn"
      class:voted={hasVoted}
      onclick={handleVote}
      disabled={voting || checkingVote || !authState.user}
      title={hasVoted ? 'Remove vote' : 'Upvote this request'}
    >
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
      </svg>
    </button>
    <span class="vote-count">{request.voteCount}</span>
  </div>

  <div class="content">
    <div class="header">
      <h3 class="title">{request.title}</h3>
      <span class="status-badge" style="background: {getStatusInfo(request.status).color}">
        {getStatusInfo(request.status).label}
      </span>
    </div>

    <p class="description">{request.description}</p>

    <div class="meta">
      <span class="category">{getCategoryLabel(request.category)}</span>
      <span class="author">by {request.authorName}</span>
      <span class="date">{formatDate(request.createdAt)}</span>
    </div>
  </div>
</div>

<style>
  .card {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
    font-family: inherit;
  }

  .card:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
  }

  .vote-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .vote-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: #6b7280;
  }

  .vote-btn:hover:not(:disabled) {
    border-color: #667eea;
    color: #667eea;
  }

  .vote-btn.voted {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: transparent;
    color: white;
  }

  .vote-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .vote-btn svg {
    width: 20px;
    height: 20px;
  }

  .vote-count {
    font-size: 0.875rem;
    font-weight: 600;
    color: #333;
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  .header {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .title {
    flex: 1;
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    line-height: 1.4;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    color: white;
    flex-shrink: 0;
  }

  .description {
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    color: #666;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: #999;
  }

  .category {
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    color: #666;
  }

  @media (max-width: 480px) {
    .card {
      flex-direction: column;
    }

    .vote-section {
      flex-direction: row;
      gap: 0.5rem;
    }

    .header {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
</style>
